import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { callApi } from 'c-data-layer';
import ApiClient from 'c-data/apiClient';

// A per-provider cache for POST /reporting/dynamic responses. Widgets that need the
// exact same request (same metrics/dimensions/filters/sort/limit) share a single
// network call instead of each firing their own.

export type DashboardReportRequest = {
    graphType: string;
    metrics: { field: string; aggregation: string }[];
    dimensions: { field: string; granularity?: string }[];
    filters: { field: string; operator: string; value: string | number | string[] }[];
    sort: { field: string; direction: 'asc' | 'desc' }[];
    limit: number;
    // When set, the widget queries this saved datasource
    // (POST /reporting/datasource/:id/query) instead of the generic /reporting/dynamic.
    datasourceId?: string | number;
};

type Entry = { loading: boolean; data?: any; error?: any };

type ContextValue = {
    get: (key: string) => Entry | undefined;
    ensure: (key: string, request: DashboardReportRequest) => void;
};

const DashboardReportContext = createContext<ContextValue | null>(null);

export const DashboardReportProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const dispatch = useDispatch();
    const [entries, setEntries] = useState<Record<string, Entry>>({});
    const entriesRef = useRef<Record<string, Entry>>({});

    const setEntry = useCallback((key: string, entry: Entry) => {
        entriesRef.current = { ...entriesRef.current, [key]: entry };
        setEntries(entriesRef.current);
    }, []);

    const ensure = useCallback(
        (key: string, request: DashboardReportRequest) => {
            // Already cached or in flight for this exact request — nothing to do.
            if (entriesRef.current[key]) return;
            setEntry(key, { loading: true });

            const { datasourceId, ...reportRequest } = request;
            // A widget bound to a datasource queries its saved table; otherwise fall back
            // to the generic dynamic report. Both share the same payload + response shape.
            const { request: axiosRequest } =
                datasourceId != null
                    ? ApiClient.Reports.datasourceQuery({
                          datasource: datasourceId,
                          ...(reportRequest as any),
                      })
                    : ApiClient.Reports.dashboard(reportRequest as any);
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore - thunk dispatch typing, mirrors useAPIClientRequest
            dispatch(callApi(axiosRequest))
                .then(([err, , response]: [any, any, any]) => {
                    setEntry(key, {
                        loading: false,
                        data: err ? undefined : response,
                        error: err ?? undefined,
                    });
                    return undefined;
                })
                .catch((err: any) => {
                    setEntry(key, { loading: false, error: err });
                });
        },
        [dispatch, setEntry],
    );

    const get = useCallback((key: string) => entries[key], [entries]);

    const value = useMemo<ContextValue>(() => ({ get, ensure }), [get, ensure]);

    return (
        <DashboardReportContext.Provider value={value}>{children}</DashboardReportContext.Provider>
    );
};

export const useDashboardReport = (request: DashboardReportRequest | null) => {
    const ctx = useContext(DashboardReportContext);
    const key = useMemo(() => (request ? JSON.stringify(request) : null), [request]);

    useEffect(() => {
        if (ctx && key && request) ctx.ensure(key, request);
        // request identity is captured by `key`
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ctx, key]);

    const entry = ctx && key ? ctx.get(key) : undefined;
    return {
        data: entry?.data,
        isLoading: !entry || entry.loading,
        error: entry?.error,
    };
};
